import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Send as SendIcon,
  Receipt as ReceiptIcon,
  Error as ErrorIcon,
  CheckCircle as CheckIcon,
  CreditScore as CreditNoteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import PaginatedTable from '../components/common/PaginatedTable';
import { facturacionApi, ComprobanteLog } from '../api/facturacion.api';
import { sunatApi } from '../api/sunat.api';
import api from '../api/axios';
import { getIgvRate } from '../config/igv';
import { useTiposDocumento } from '../hooks/useTiposDocumento';

const tipoDocMap: Record<string, string> = {
  '01': 'Factura',
  '03': 'Boleta',
  '07': 'Nota Credito',
  '08': 'Nota Debito',
};

const motivosNC = [
  { value: '01', label: 'Anulacion de la operacion' },
  { value: '02', label: 'Anulacion por error en el RUC' },
  { value: '06', label: 'Devolucion total' },
  { value: '07', label: 'Devolucion por item' },
];

const Facturacion: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [logs, setLogs] = useState<ComprobanteLog[]>([]);
  const [fallidos, setFallidos] = useState<ComprobanteLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // NC Dialog
  const [ncOpen, setNcOpen] = useState(false);
  const [ncOrderId, setNcOrderId] = useState('');
  const [ncMotivo, setNcMotivo] = useState('01');
  const [ncDescripcion, setNcDescripcion] = useState('Anulacion de la operacion');
  const [ncLoading, setNcLoading] = useState(false);
  const [pedidosPagados, setPedidosPagados] = useState<any[]>([]);

  // Edit Dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editLogId, setEditLogId] = useState<number | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [buscarClienteLoading, setBuscarClienteLoading] = useState(false);
  const [editCliente, setEditCliente] = useState({ nombre: '', documento: '', tipoEntidad: '1' });
  const [editItems, setEditItems] = useState<Array<{ cantidad: string; producto: string; precio_base: string }>>([]);
  const [editPayload, setEditPayload] = useState<any>(null);
  const [printLoadingId, setPrintLoadingId] = useState<number | null>(null);

  const { tipos: tiposDocumento } = useTiposDocumento();
  const tipoEntidad = tiposDocumento.find((t) => t.codigo === editCliente.tipoEntidad);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await facturacionApi.getLogs();
      setLogs(data.data || []);
      setTotal(data.total || 0);
    } catch { setLogs([]); }
    setLoading(false);
  }, []);

  const loadFallidos = useCallback(async () => {
    try {
      const { data } = await facturacionApi.getFallidos();
      setFallidos(Array.isArray(data) ? data : []);
    } catch { setFallidos([]); }
  }, []);

  const loadPedidosPagados = useCallback(async () => {
    try {
      const { data } = await api.get('/orders', { params: { status: 'pagado' } });
      setPedidosPagados(
        (Array.isArray(data) ? data : []).filter(
          (o: any) => o.estadoSunat === 'ACEPTADO' && o.serieComprobante,
        ),
      );
    } catch { setPedidosPagados([]); }
  }, []);

  useEffect(() => {
    loadLogs();
    loadFallidos();
    loadPedidosPagados();
  }, [loadLogs, loadFallidos, loadPedidosPagados]);

  const buscarClienteDoc = async () => {
    const doc = editCliente.documento.trim();
    if (!doc) {
      setSnack({ open: true, message: 'Ingresa el numero de documento.', severity: 'error' });
      return;
    }
    const esDni = editCliente.tipoEntidad === '1';
    const esRuc = editCliente.tipoEntidad === '6';
    if (esDni && doc.length !== 8) {
      setSnack({ open: true, message: 'El DNI debe tener 8 digitos.', severity: 'error' });
      return;
    }
    if (esRuc && doc.length !== 11) {
      setSnack({ open: true, message: 'El RUC debe tener 11 digitos.', severity: 'error' });
      return;
    }
    if (!esDni && !esRuc) {
      setSnack({ open: true, message: 'Solo se puede buscar DNI (1) o RUC (6) automaticamente.', severity: 'error' });
      return;
    }
    setBuscarClienteLoading(true);
    try {
      const res = esDni ? await sunatApi.consultarDni(doc) : await sunatApi.consultarRuc(doc);
      const data = res.data;
      if (data?.success && data.fullName) {
        setEditCliente({ ...editCliente, nombre: data.fullName });
        setSnack({ open: true, message: 'Cliente autocompletado desde SUNAT.', severity: 'success' });
      } else {
        setSnack({ open: true, message: data?.message || 'No se encontro el documento.', severity: 'error' });
      }
    } catch (err: any) {
      setSnack({ open: true, message: err?.response?.data?.message || 'Error consultando el documento.', severity: 'error' });
    } finally {
      setBuscarClienteLoading(false);
    }
  };

  const handleEditar = async (logId: number) => {
    try {
      const { data } = await facturacionApi.getDetalle(logId);
      if (data && data.requestJson) {
        const payload = JSON.parse(data.requestJson);
        setEditPayload(payload);
        setEditLogId(logId);
        setEditCliente({
          nombre: payload.cliente?.razon_social_nombres || '',
          documento: payload.cliente?.numero_documento || '',
          tipoEntidad: payload.cliente?.codigo_tipo_entidad || '1',
        });
        setEditItems(
          (payload.items || []).map((item: any) => ({
            cantidad: item.cantidad || '1',
            producto: item.producto || '',
            precio_base: item.precio_base || '0.00',
          })),
        );
        setEditOpen(true);
      }
    } catch {
      setSnack({ open: true, message: 'Error al cargar datos', severity: 'error' });
    }
  };

  const handleGuardarEdicion = async () => {
    if (!editLogId || !editPayload) return;
    setEditLoading(true);
    try {
      let sumaGravada = 0;
      const itemsCorregidos = editItems.map((item) => {
        const base = parseFloat(item.precio_base) || 0;
        const qty = parseFloat(item.cantidad) || 1;
        sumaGravada += base * qty;
        return { ...item, codigo_unidad: 'ZZ', tipo_igv_codigo: '10' };
      });
      const totalIgv = Math.round(sumaGravada * getIgvRate() * 100) / 100;
      const nuevoPayload = {
        ...editPayload,
        cliente: { ...editPayload.cliente, codigo_tipo_entidad: editCliente.tipoEntidad, numero_documento: editCliente.documento, razon_social_nombres: editCliente.nombre },
        venta: { ...editPayload.venta, total_gravada: sumaGravada.toFixed(2), total_igv: totalIgv.toFixed(2), total_a_pagar: (sumaGravada + totalIgv).toFixed(2) },
        items: itemsCorregidos,
      };
      const { data } = await facturacionApi.editarReenviar(editLogId, nuevoPayload);
      setSnack({ open: true, message: data.success ? `Reenviado: ${data.message}` : `Error: ${data.message}`, severity: data.success ? 'success' : 'error' });
      setEditOpen(false);
      loadLogs();
      loadFallidos();
    } catch (err: any) {
      setSnack({ open: true, message: err.response?.data?.message || 'Error', severity: 'error' });
    }
    setEditLoading(false);
  };

  const handleImprimir = async (logId: number) => {
    const printWindow = window.open('', '_blank', 'width=920,height=760');
    if (!printWindow) {
      setSnack({ open: true, message: 'Permite las ventanas emergentes para imprimir el comprobante.', severity: 'error' });
      return;
    }
    printWindow.document.write('<p style="font-family: Arial; padding: 24px;">Generando comprobante...</p>');
    setPrintLoadingId(logId);
    try {
      const { data } = await facturacionApi.getImpresion(logId);
      printWindow.document.open();
      printWindow.document.write(typeof data === 'string' ? data : '');
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        try {
          printWindow.print();
        } catch {
          // El navegador puede bloquear la impresion automatica.
        }
      }, 400);
    } catch (err: any) {
      printWindow.close();
      setSnack({
        open: true,
        message: err?.response?.data?.message || 'No se pudo generar el comprobante.',
        severity: 'error',
      });
    } finally {
      setPrintLoadingId(null);
    }
  };

  const handleReenviar = async (logId: number) => {
    try {
      await facturacionApi.reenviar(logId);
      setSnack({ open: true, message: 'Reenviado exitosamente', severity: 'success' });
      loadLogs();
      loadFallidos();
    } catch (err: any) {
      setSnack({ open: true, message: err.response?.data?.message || 'Error', severity: 'error' });
    }
  };

  const handleEmitirNC = async () => {
    if (!ncOrderId) return;
    setNcLoading(true);
    try {
      const { data } = await facturacionApi.emitirNotaCredito(Number(ncOrderId), ncMotivo, ncDescripcion);
      setSnack({
        open: true,
        message: data.success ? `NC ${data.serie}-${data.numero}: ${data.mensaje}` : `Error: ${data.mensaje}`,
        severity: data.success ? 'success' : 'error',
      });
      setNcOpen(false);
      loadLogs();
    } catch (err: any) {
      setSnack({ open: true, message: err.response?.data?.message || 'Error', severity: 'error' });
    }
    setNcLoading(false);
  };

  // Columnas para la tabla
  const allColumns = [
    { id: 'id', label: 'ID', minWidth: 50 },
    {
      id: 'tipoDocumento', label: 'Tipo', minWidth: 90,
      format: (v: string) => (
        <Chip label={tipoDocMap[v] || v} size="small" color={v === '07' ? 'secondary' : 'primary'} variant="outlined" />
      ),
    },
    {
      id: 'serie', label: 'Comprobante', minWidth: 140,
      format: (_: any, row: any) => <strong>{row.serie}-{row.numero}</strong>,
    },
    { id: 'fechaEmision', label: 'Fecha', minWidth: 100 },
    {
      id: 'clienteNombre', label: 'Cliente', minWidth: 150,
      format: (v: string, row: any) => (
        <Box>
          <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>{v}</Typography>
          <Typography variant="caption" color="text.secondary">{row.clienteDocumento}</Typography>
        </Box>
      ),
    },
    {
      id: 'totalPagar', label: 'Total', align: 'right' as const, minWidth: 90,
      format: (v: any) => <strong>S/ {Number(v).toFixed(2)}</strong>,
    },
    {
      id: 'estado', label: 'Estado', minWidth: 120,
      format: (v: string) => {
        const colorMap: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
          ACEPTADO: 'success', RECHAZADO: 'error', ERROR_ENVIO: 'warning', PENDIENTE: 'default', REENVIADO: 'warning', ANULADO: 'default',
        };
        return <Chip label={v} size="small" color={colorMap[v] || 'default'} icon={v === 'ACEPTADO' ? <CheckIcon /> : v === 'RECHAZADO' || v === 'ERROR_ENVIO' ? <ErrorIcon /> : undefined} />;
      },
    },
    {
      id: 'mensajeSunat', label: 'Mensaje SUNAT', minWidth: 200,
      format: (v: string, row: any) => (
        <Typography variant="caption" sx={{ maxWidth: 250, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: row.estado === 'ACEPTADO' ? 'success.main' : 'error.main' }}>
          {v || row.errorDetalle || '-'}
        </Typography>
      ),
    },
    { id: 'intentos', label: 'Intentos', align: 'center' as const, minWidth: 70 },
  ];

  const renderPrintAction = (row: any) => (
    <Tooltip title="Imprimir comprobante">
      <span>
        <IconButton
          color="primary"
          size="small"
          onClick={() => handleImprimir(row.id)}
          disabled={printLoadingId === row.id}
        >
          {printLoadingId === row.id ? <CircularProgress size={18} /> : <PrintIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Facturacion Electronica</Typography>
          <Typography variant="body2" color="text.secondary">Comprobantes emitidos a SUNAT ({total} total)</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { loadLogs(); loadFallidos(); }}>
            Actualizar
          </Button>
          <Button variant="contained" startIcon={<CreditNoteIcon />} color="secondary" onClick={() => { setNcOpen(true); loadPedidosPagados(); }}>
            Nota de Credito
          </Button>
        </Box>
      </Box>

      {/* Resumen */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {[
          { label: 'Aceptados', count: logs.filter((l) => l.estado === 'ACEPTADO').length, color: '#10B981' },
          { label: 'Rechazados', count: logs.filter((l) => l.estado === 'RECHAZADO').length, color: '#EF4444' },
          { label: 'Error Envio', count: logs.filter((l) => l.estado === 'ERROR_ENVIO').length, color: '#F59E0B' },
        ].map((item) => (
          <Paper key={item.label} sx={{ p: 2, flex: 1, borderLeft: `4px solid ${item.color}` }}>
            <Typography variant="h4" fontWeight={700}>{item.count}</Typography>
            <Typography variant="body2" color="text.secondary">{item.label}</Typography>
          </Paper>
        ))}
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab icon={<ReceiptIcon />} iconPosition="start" label={`Todos (${logs.length})`} />
          <Tab icon={<ErrorIcon />} iconPosition="start" label={`Fallidos (${fallidos.length})`} />
        </Tabs>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          {tab === 0 && (
            <PaginatedTable
              columns={allColumns}
              rows={logs}
              exportFileName="comprobantes_sunat"
              searchFields={['serie', 'numero', 'clienteNombre', 'clienteDocumento', 'estado']}
              emptyMessage="No hay comprobantes"
              renderActions={renderPrintAction}
            />
          )}
          {tab === 1 && (
            <>
              {fallidos.length > 0 && (
                <Alert severity="info" sx={{ m: 2 }}>
                  Revisa el error de SUNAT, edita los datos si es necesario, y reenvía.
                </Alert>
              )}
              <PaginatedTable
                columns={allColumns}
                rows={fallidos}
                exportFileName="comprobantes_fallidos"
                searchFields={['serie', 'numero', 'clienteNombre', 'mensajeSunat']}
                emptyMessage="No hay comprobantes fallidos"
                renderActions={(row) => (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {renderPrintAction(row)}
                    <Tooltip title="Editar y reenviar">
                      <IconButton color="warning" size="small" onClick={() => handleEditar(row.id)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reenviar sin cambios">
                      <IconButton color="primary" size="small" onClick={() => handleReenviar(row.id)}>
                        <SendIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              />
            </>
          )}
        </>
      )}

      {/* Dialog Nota de Crédito */}
      <Dialog open={ncOpen} onClose={() => setNcOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Emitir Nota de Credito</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecciona el pedido con comprobante aceptado y el motivo.
          </Typography>
          <TextField select fullWidth label="Pedido con comprobante" value={ncOrderId} onChange={(e) => setNcOrderId(e.target.value)} sx={{ mb: 2, mt: 1 }}>
            {pedidosPagados.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.code} - {o.tipoComprobante === '01' ? 'Factura' : 'Boleta'} {o.serieComprobante}-{o.numeroComprobante} - S/ {Number(o.total).toFixed(2)}
              </MenuItem>
            ))}
            {pedidosPagados.length === 0 && <MenuItem disabled>No hay pedidos con comprobante aceptado</MenuItem>}
          </TextField>
          <TextField select fullWidth label="Motivo" value={ncMotivo} onChange={(e) => { setNcMotivo(e.target.value); const m = motivosNC.find((x) => x.value === e.target.value); if (m) setNcDescripcion(m.label); }} sx={{ mb: 2 }}>
            {motivosNC.map((m) => <MenuItem key={m.value} value={m.value}>{m.value} - {m.label}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="Descripcion" value={ncDescripcion} onChange={(e) => setNcDescripcion(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNcOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="secondary" onClick={handleEmitirNC} disabled={!ncOrderId || ncLoading} startIcon={ncLoading ? <CircularProgress size={18} /> : <CreditNoteIcon />}>
            Emitir Nota de Credito
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Editar Comprobante */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Editar y Reenviar Comprobante #{editLogId}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>Corrige los datos y presiona "Guardar y Reenviar".</Alert>
          <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Datos del Cliente</Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField select label="Tipo Doc." value={editCliente.tipoEntidad} onChange={(e) => setEditCliente({ ...editCliente, tipoEntidad: e.target.value, documento: '' })} sx={{ width: 190 }} size="small">
              {tiposDocumento.map((t) => (
                <MenuItem key={t.codigo} value={t.codigo}>{t.nombre}</MenuItem>
              ))}
            </TextField>
            <TextField label="Nro. Documento" value={editCliente.documento} onChange={(e) => setEditCliente({ ...editCliente, documento: e.target.value })} size="small" sx={{ width: 180 }} inputProps={{ maxLength: tipoEntidad?.maxLength || 15 }} />
            <Tooltip title="Buscar en SUNAT">
              <IconButton size="small" color="primary" onClick={buscarClienteDoc} disabled={buscarClienteLoading}>
                {buscarClienteLoading ? <CircularProgress size={18} /> : <SearchIcon />}
              </IconButton>
            </Tooltip>
            <TextField label="Nombre / Razon Social" value={editCliente.nombre} onChange={(e) => setEditCliente({ ...editCliente, nombre: e.target.value })} size="small" fullWidth />
          </Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Items</Typography>
          {editItems.map((item, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 1 }}>
              <TextField label="Cantidad" value={item.cantidad} onChange={(e) => { const c = [...editItems]; c[idx] = { ...c[idx], cantidad: e.target.value }; setEditItems(c); }} size="small" sx={{ width: 100 }} />
              <TextField label="Producto" value={item.producto} onChange={(e) => { const c = [...editItems]; c[idx] = { ...c[idx], producto: e.target.value }; setEditItems(c); }} size="small" fullWidth />
              <TextField label="Precio Base (sin IGV)" value={item.precio_base} onChange={(e) => { const c = [...editItems]; c[idx] = { ...c[idx], precio_base: e.target.value }; setEditItems(c); }} size="small" sx={{ width: 160 }} />
            </Box>
          ))}
          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">Los totales se recalculan automaticamente al guardar.</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleGuardarEdicion} disabled={editLoading} startIcon={editLoading ? <CircularProgress size={18} /> : <SendIcon />}>
            Guardar y Reenviar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack({ ...snack, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Facturacion;
